import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import {
  SAPSection,
  OomPahPlayer,
  SAPChordRow,
  SAPCallout,
  SAPQuote,
  SAPPracticeLadder,
} from "@/components/sap/SAPShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { NoteImage, NoteImagePair } from "@/components/mt/NoteImage";

export default function SingAndPlay2() {
  const { lesson, course, accent } = findLesson("sing-and-play", "2");
  return (
    <LessonShell
      course={course}
      courseSlug="sing-and-play"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/sing-and-play/SP2-Oom-Pah-Susanna-Animated.mp4"
        caption={`Fast chord changes — "Oh Susanna" swaps mid-bar`}
        hint="≈ 26 sec · two chords per bar"
      />
      {/* ── 1. Context ─────────────────────────────────────────────────── */}
      <SAPSection eyebrow="The big idea" title="Oh Susanna — fast changes, loose grip">
        <p>
          You know the Oom-Pah now. The left hand is the engine; the right hand is the passenger.
          In Lesson 1, I'll Fly Away gave you comfortable, wide-open chord changes — two bars on D
          before anything moved. In Oh Susanna, the changes come <em>fast</em>. Sometimes within
          a single bar. That's the new challenge.
        </p>
        <p>
          The secret? <strong>Keep the left hand going no matter what.</strong> If the right hand
          misses a chord, don't stop — just catch the next one. The groove lives in the left
          hand. Miss a chord with the right and you've lost a colour. Miss the beat with the left
          and you've lost the song.
        </p>
        <SAPQuote>
          If it's a quick change, perhaps don't do the Oom-Pah at that specific point.
        </SAPQuote>
      </SAPSection>

      {/* ── 2. Reference recording ─────────────────────────────────────── */}
      <SAPSection eyebrow="Listen first" title="Stephen Foster's Oh Susanna">
        <p>
          Stephen Foster wrote this in 1847 — one of the earliest pop songs in American history.
          Listen to how the melody bounces between the tonic (G) and the dominant (D7). The
          phrases are short and symmetrical. Every two bars brings a new thought. You'll feel
          those quick changes in your body before you understand them on paper.
        </p>
        <YouTubeEmbed
          videoId="GUPRVkhBtVA"
          title="Oh Susanna — Stephen Foster"
          caption="One of the great American folk songs. Listen to the short, quick chord changes."
        />
      </SAPSection>

      {/* ── 3. The chords ──────────────────────────────────────────────── */}
      <SAPSection eyebrow="The harmony" title="G major — I, IV, V7 moving fast">
        <p>
          The whole song lives in G major. Three chords: G (I), C (IV), and D7 (V7). What makes
          it tricky is that some bars change chords on beat 3 — a "quick change" — not just at
          the top of the bar.
        </p>
        <p>
          In the verse, <em>"I come from Alabama with my banjo on my knee"</em> — the first four
          bars are mostly G, with D7 arriving on the second half of bar 4. Then in bar 5
          (<em>"Oh Susanna"</em>), a C chord pops in for just half a bar before landing back on G.
          These quick changes are where students come undone.
        </p>

        <SAPChordRow
          label="Verse chords · G major"
          chords={[
            { label: "G",  rn: "I",   pitches: ["G3","B3","D4","G4"], fn: "tonic" },
            { label: "C",  rn: "IV",  pitches: ["C4","E4","G4","C5"], fn: "subdominant" },
            { label: "D7", rn: "V7",  pitches: ["D3","F#3","A3","C4"], fn: "dominant" },
          ]}
          caption="Three chords, that's it. The challenge is switching quickly between them."
        />

        <RomanNumeralAnalysis
          label="Verse · G major"
          caption="Two-bar phrases that move quickly. G holds for the opening statement, D7 pulls you back to G at the cadence."
          chords={[
            { chord: "G",  rn: "I",   function: "Tonic",        pitches: ["G3","B3","D4","G4"] },
            { chord: "G",  rn: "I",   function: "Tonic",        pitches: ["G3","B3","D4","G4"] },
            { bar: true },
            { chord: "G",  rn: "I",   function: "Tonic",        pitches: ["G3","B3","D4","G4"] },
            { chord: "D7", rn: "V7",  function: "Dominant",     pitches: ["D3","F#3","A3","C4"] },
            { bar: true },
            { chord: "G",  rn: "I",   function: "Tonic",        pitches: ["G3","B3","D4","G4"] },
            { chord: "C",  rn: "IV",  function: "Pre-dominant", pitches: ["C4","E4","G4","C5"] },
            { bar: true },
            { chord: "G",  rn: "I",   function: "Tonic",        pitches: ["G3","B3","D4","G4"] },
            { chord: "D7", rn: "V7",  function: "Dominant",     pitches: ["D3","F#3","A3","C4"] },
            { bar: true },
            { chord: "G",  rn: "I",   function: "Tonic",        pitches: ["G3","B3","D4","G4"] },
          ]}
        />
      </SAPSection>

      {/* ── 4. Interactive loop ────────────────────────────────────────── */}
      <SAPSection eyebrow="Interactive" title="The Oom-Pah loop — G major">
        <p>
          This loop plays the basic I–V7–I skeleton. Notice how tight the D7 feels when it
          arrives — only one bar before resolving back to G. Set the BPM to 80 and just listen
          first. Then try to play along with just your left hand.
        </p>
        <OomPahPlayer
          progression={[
            { label: "G",  bars: 2, lhRoot: "G3", lhFifth: "D4", rh: ["G4","B4","D5"] },
            { label: "D7", bars: 1, lhRoot: "D3", lhFifth: "A3", rh: ["F#4","A4","C5"] },
            { label: "G",  bars: 1, lhRoot: "G3", lhFifth: "D4", rh: ["G4","B4","D5"] },
            { label: "C",  bars: 1, lhRoot: "C4", lhFifth: "G4", rh: ["E4","G4","C5"] },
            { label: "G",  bars: 1, lhRoot: "G3", lhFifth: "D4", rh: ["G4","B4","D5"] },
          ]}
          bpm={85}
          label="OH SUSANNA — G major · 6-bar loop"
        />
        <SAPCallout label="Quick-change rule" variant="insight">
          When the chord changes on beat 3 or later in a bar, don't fight the Oom-Pah to fit
          both chords in. Just <strong>play a block chord on the change beat</strong> and resume
          the pattern at the top of the next bar. Three clean chords beat six muddy ones every
          time.
        </SAPCallout>
      </SAPSection>

      {/* ── 5. The quick-change technique ──────────────────────────────── */}
      <SAPSection eyebrow="The technique" title="How to handle a mid-bar chord change">
        <p>
          A quick change is when the harmony shifts on beat 3 of a 4-beat bar. You only have two
          beats to play Oom-Pah on the new chord before the bar ends. Options:
        </p>
        <SAPCallout label="Option A — Block chord" variant="tip">
          On the quick-change beat, play the new chord as a solid block in both hands, then
          continue Oom-Pah on the next bar. Clean, musical, professional. Jason's first choice.
        </SAPCallout>
        <SAPCallout label="Option B — Half-bar Oom-Pah" variant="tip">
          Play Oom-Pah on beats 3 and 4 of the new chord: one bass note, one off-beat chord.
          Works if your chord change is fast and confident. Risks muddying the rhythm if you
          hesitate.
        </SAPCallout>
        <p>
          Practice both. The block chord is safer; the half-bar Oom-Pah feels more musical once
          you own it. But never choose style over groove — a stumbled half-bar Oom-Pah breaks
          the song.
        </p>

      </SAPSection>

      {/* ── 6. The chorus ──────────────────────────────────────────────── */}
      <SAPSection eyebrow="The chorus" title="Oh Susanna — same chords, same lesson">
        <p>
          The chorus (<em>"Oh Susanna, oh don't you cry for me"</em>) uses the same G–C–D7
          vocabulary. The quick change on <em>"cry for me"</em> brings in C for half a bar. Same
          rule applies: block chord or half-bar Oom-Pah. The chorus phrase is shorter, so it
          feels even faster. That's the real workout.
        </p>
        <SAPCallout label="Chorus tip" variant="tip">
          In the chorus, aim to keep the LH bass running on every downbeat even through chord
          changes. The rhythm of the chorus (<em>ONE two THREE four</em>) is close to a march —
          never let the bass drop out. Think of your foot tapping; the LH is that foot.
        </SAPCallout>
      </SAPSection>

      {/* ── 7. Practice plan ───────────────────────────────────────────── */}

      {/* ── Jason's notes ────────────────────────────────────────────── */}
      <SAPSection eyebrow="Jason's chord chart" title="Handwritten teaching notes">
        <p>
          This is Jason's original handwritten chord chart for this song — the same one he
          uses in the video lesson. Click to zoom in and follow along.
        </p>
        <NoteImage
          src="/notes/sing-and-play/02-oh-suzannah.png"
          caption="Jason's chord chart — Oh Susanna in G major (D major in some versions)"
        />
      </SAPSection>
      <SAPSection eyebrow="Practice plan" title="Build the speed gradually">
        <SAPPracticeLadder
          intro="Don't rush the tempo. Accuracy at 70 bpm is worth more than guessing at 120. The speed will come."
          rungs={[
            {
              title: "Slow chord changes",
              minutes: 5,
              detail: "G → C → D7 → G, block chords only, both hands together at 60 bpm. No Oom-Pah yet — just hear the harmony.",
              accent: "amber",
            },
            {
              title: "LH bass pattern alone",
              minutes: 5,
              detail: "G–D–G–D alternating bass at 70 bpm. Change to C and D7 bass when the chord changes. Left hand only.",
              accent: "amber",
            },
            {
              title: "Full Oom-Pah, slow changes only",
              minutes: 8,
              detail: "Play bars where the chord only changes at the top of the bar with full Oom-Pah. Use block chords on quick-change bars.",
              accent: "purple",
            },
            {
              title: "Add quick-change half-bar Oom-Pah",
              minutes: 8,
              detail: "Now attempt the half-bar Oom-Pah on the quick-change bars. If it stumbles, go back to block chords.",
              accent: "purple",
            },
            {
              title: "Sing the verse and chorus",
              minutes: 10,
              detail: "Put it all together at 80 bpm. Hum first, then add words. The pattern is locked; the melody floats.",
              accent: "emerald",
            },
          ]}
        />
      </SAPSection>
    </LessonShell>
  );
}
